/**
 * Query Performance Overlay.
 *
 * Floating dev panel that shows real-time status and timing of all API
 * calls on the current page, including the SQL queries each endpoint ran.
 * Only visible to david.turton@wmplp.com (or in demo mode).
 * Toggle with click or Ctrl+Shift+Q.
 */

import { useState, useEffect, useSyncExternalStore, useCallback } from "react";
import { SERIES } from "../utils/chartTheme";
import {
  subscribe,
  getSnapshot,
  clear,
  type TrackedQuery,
  type SqlQuery,
} from "../utils/queryTracker";

const ALLOWED_EMAIL = "david.turton@wmplp.com";

/**
 * Check whether the overlay should be visible.
 * In demo mode (no MSAL) — always show (local dev).
 * In live mode — only show for the allowed email.
 */
function useIsAllowedUser(): boolean {
  const [allowed, setAllowed] = useState(false);

  useEffect(() => {
    function check(): boolean {
      try {
        const { msalInstance } = require("../index");
        if (!msalInstance) {
          setAllowed(true);
          return true;
        }
        const account = msalInstance.getActiveAccount?.();
        if (account?.username?.toLowerCase() === ALLOWED_EMAIL) {
          setAllowed(true);
          return true;
        }
        if (account) return true;
      } catch {
        setAllowed(true);
        return true;
      }
      return false;
    }

    if (check()) return;
    const id = setInterval(() => {
      if (check()) clearInterval(id);
    }, 500);
    return () => clearInterval(id);
  }, []);

  return allowed;
}

// ── Subcomponents ──────────────────────────────────────────────────────

function StatusIcon({ status }: { status: TrackedQuery["status"] }) {
  if (status === "pending") {
    return (
      <span className="inline-block h-3 w-3 rounded-full border-2 border-amber-400 border-t-transparent animate-spin" />
    );
  }
  if (status === "loaded") {
    return <span className="text-emerald-400 text-xs font-bold">&#10003;</span>;
  }
  return <span className="text-red-400 text-xs font-bold">&#10007;</span>;
}

function TimingLabel({ query }: { query: TrackedQuery }) {
  if (query.status === "pending") {
    const elapsed = Math.round(performance.now() - query.startTime);
    return <span className="text-amber-300 text-xs">{elapsed}ms...</span>;
  }

  const network = query.networkMs != null ? `${query.networkMs}ms` : "?";
  const server =
    query.serverMs != null ? (
      <span className="text-slate-400"> ({query.serverMs}ms server)</span>
    ) : null;

  return (
    <span className="text-xs text-slate-300">
      {network}
      {server}
    </span>
  );
}

function SqlBadge({ sq }: { sq: SqlQuery }) {
  return (
    <span
      className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-medium ${
        sq.cached ? "bg-sky-900/50 text-sky-300" : "bg-slate-700 text-slate-300"
      }`}
    >
      {sq.cached ? "CACHE" : `${sq.durationMs}ms`}
      <span className="text-slate-400">{sq.rowCount}r</span>
    </span>
  );
}

function formatSql(raw: string): string {
  // Light formatting: collapse whitespace, keep readable
  return raw.replace(/\s+/g, " ").trim();
}

function SqlDetail({ queries: sqList }: { queries: SqlQuery[] }) {
  if (sqList.length === 0) return null;

  return (
    <div className="mt-1 ml-6 space-y-1.5">
      {sqList.map((sq, i) => (
        <div
          key={i}
          className="rounded bg-slate-800/70 border border-slate-700/50 px-2 py-1.5"
        >
          <div className="flex items-center gap-2 mb-1">
            <SqlBadge sq={sq} />
            {sq.params && (
              <span className="text-[10px] text-slate-500 truncate">
                {Object.entries(sq.params)
                  .map(([k, v]) => `@${k}=${v}`)
                  .join(", ")}
              </span>
            )}
          </div>
          <pre className="text-[11px] leading-relaxed text-slate-300 whitespace-pre-wrap break-words font-mono">
            {formatSql(sq.sql)}
          </pre>
        </div>
      ))}
    </div>
  );
}

function QueryRow({
  query,
  expandedId,
  onToggle,
}: {
  query: TrackedQuery;
  expandedId: number | null;
  onToggle: (id: number) => void;
}) {
  const isOpen = expandedId === query.id;
  const hasSql = query.sqlQueries.length > 0;

  return (
    <div className="px-1 py-0.5">
      <div
        className={`flex items-center gap-2 px-2 py-1.5 rounded text-xs ${
          hasSql ? "cursor-pointer hover:bg-slate-800" : ""
        }`}
        onClick={() => hasSql && onToggle(query.id)}
      >
        <StatusIcon status={query.status} />
        {hasSql && (
          <span className="text-slate-600 text-[10px] w-3 shrink-0">
            {isOpen ? "▼" : "▶"}
          </span>
        )}
        <span className="text-slate-500 w-8 shrink-0 text-right">
          {query.method}
        </span>
        <span
          className="flex-1 truncate font-mono text-slate-200"
          title={query.url}
        >
          {query.url}
        </span>
        {hasSql && (
          <span className="text-slate-600 text-[10px] shrink-0">
            {query.sqlQueries.length} sql
          </span>
        )}
        {query.httpStatus && query.status === "error" && (
          <span className="text-red-400 shrink-0">{query.httpStatus}</span>
        )}
        <TimingLabel query={query} />
      </div>
      {isOpen && <SqlDetail queries={query.sqlQueries} />}
    </div>
  );
}

// ── Main Component ─────────────────────────────────────────────────────

type PanelSize = "normal" | "large" | "full";

const SIZE_CLASSES: Record<PanelSize, string> = {
  normal: "w-[480px] max-h-[50vh]",
  large: "w-[720px] max-h-[80vh]",
  full: "w-[95vw] max-h-[90vh]",
};

const NEXT_SIZE: Record<PanelSize, PanelSize> = {
  normal: "large",
  large: "full",
  full: "normal",
};

const SIZE_LABELS: Record<PanelSize, string> = {
  normal: "▣",
  large: "◻",
  full: "▫",
};

export function QueryOverlay() {
  const queries = useSyncExternalStore(subscribe, getSnapshot);
  const [expanded, setExpanded] = useState(false);
  const [size, setSize] = useState<PanelSize>("normal");
  const [expandedQueryId, setExpandedQueryId] = useState<number | null>(null);
  const isAllowed = useIsAllowedUser();

  // Re-render periodically while any query is pending
  const hasPending = queries.some((q) => q.status === "pending");
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!hasPending) return;
    const id = setInterval(() => setTick((t) => t + 1), 200);
    return () => clearInterval(id);
  }, [hasPending]);

  // Keyboard shortcut: Ctrl+Shift+Q
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.ctrlKey && e.shiftKey && e.key === "Q") {
      e.preventDefault();
      setExpanded((prev) => !prev);
    }
  }, []);

  useEffect(() => {
    if (!isAllowed) return;
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handleKeyDown, isAllowed]);

  const handleToggleQuery = useCallback((id: number) => {
    setExpandedQueryId((prev) => (prev === id ? null : id));
  }, []);

  if (!isAllowed) return null;

  const loaded = queries.filter((q) => q.status === "loaded").length;
  const pending = queries.filter((q) => q.status === "pending").length;
  const errors = queries.filter((q) => q.status === "error").length;
  const total = queries.length;
  const totalSql = queries.reduce((n, q) => n + q.sqlQueries.length, 0);

  if (total === 0 && !expanded) return null;

  // Collapsed badge
  if (!expanded) {
    return (
      <button
        onClick={() => setExpanded(true)}
        className="fixed bottom-4 right-4 z-50 flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium text-white shadow-lg transition-all hover:scale-105"
        style={{ backgroundColor: SERIES.primary }}
        title="Query Performance (Ctrl+Shift+Q)"
      >
        {pending > 0 && (
          <span className="h-2 w-2 rounded-full bg-amber-400 animate-pulse" />
        )}
        {errors > 0 && <span className="h-2 w-2 rounded-full bg-red-400" />}
        {pending === 0 && errors === 0 && (
          <span className="h-2 w-2 rounded-full bg-emerald-400" />
        )}
        {loaded}/{total}
      </button>
    );
  }

  // Expanded panel
  return (
    <div
      className={`fixed bottom-4 right-4 z-50 flex flex-col rounded-lg bg-slate-900 text-white shadow-2xl border border-slate-700 overflow-hidden transition-all duration-200 ${SIZE_CLASSES[size]}`}
    >
      {/* Header */}
      <div
        className="flex items-center justify-between px-4 py-2 border-b border-slate-700 select-none shrink-0"
        style={{ backgroundColor: SERIES.primary }}
      >
        <span
          className="text-sm font-semibold cursor-pointer"
          onClick={() => setExpanded(false)}
        >
          Query Performance
        </span>
        <div className="flex items-center gap-2">
          <span className="text-xs opacity-70">Ctrl+Shift+Q</span>
          <button
            onClick={() => setSize(NEXT_SIZE[size])}
            className="text-white/70 hover:text-white text-sm px-1"
            title={`Resize (${size} → ${NEXT_SIZE[size]})`}
          >
            {SIZE_LABELS[size]}
          </button>
          <button
            onClick={() => setExpanded(false)}
            className="text-white/70 hover:text-white text-sm px-1"
          >
            ✕
          </button>
        </div>
      </div>

      {/* Query list */}
      <div className="flex-1 overflow-y-auto py-1">
        {queries.length === 0 ? (
          <p className="text-slate-500 text-xs text-center py-4">
            No API calls yet
          </p>
        ) : (
          queries.map((q) => (
            <QueryRow
              key={q.id}
              query={q}
              expandedId={expandedQueryId}
              onToggle={handleToggleQuery}
            />
          ))
        )}
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between px-4 py-2 border-t border-slate-700 text-xs text-slate-400 shrink-0">
        <div className="flex gap-3">
          <span>
            API: <span className="text-white">{total}</span>
          </span>
          <span>
            SQL: <span className="text-white">{totalSql}</span>
          </span>
          <span>
            Loaded: <span className="text-emerald-400">{loaded}</span>
          </span>
          {pending > 0 && (
            <span>
              Pending: <span className="text-amber-400">{pending}</span>
            </span>
          )}
          {errors > 0 && (
            <span>
              Errors: <span className="text-red-400">{errors}</span>
            </span>
          )}
        </div>
        <button
          onClick={clear}
          className="text-slate-500 hover:text-white transition-colors"
        >
          Clear
        </button>
      </div>
    </div>
  );
}
